/**
 * Mobile Workspace tab — a plain list of the tenant's workspaces.
 * Tapping one enters the workspace surface (MobileWorkspaceScreen: Files /
 * Editor / Assistant sections) — the route param is the workspace's
 * folderId, same as desktop's /workspace/:id.
 */
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LayoutGrid } from 'lucide-react'
import { workspaceApi, getWorkspaceName } from '@papan/workspace/client'
import { useTenant } from '../hooks/useTenant'

export function MobileWorkspaceList() {
  const navigate = useNavigate()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  const { data, isLoading, error } = useQuery({
    queryKey: ['mobile-workspaces', tenantId, activeOrgEmail ?? ''],
    queryFn: () => workspaceApi.list(),
    staleTime: 30_000,
  })

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b themed-border px-4 py-2.5 shrink-0">
        <span className="text-sm font-medium themed-text">Workspaces</span>
      </div>
      <main className="flex-1 overflow-y-auto overscroll-contain">
        {isLoading && (
          <p className="px-4 py-10 text-center text-sm themed-text-muted">Loading…</p>
        )}
        {error && (
          <p className="px-4 py-10 text-center text-sm text-red-400">
            {error instanceof Error ? error.message : 'Failed to load'}
          </p>
        )}
        {data && data.length === 0 && (
          <p className="px-4 py-10 text-center text-sm themed-text-muted">No workspaces yet</p>
        )}
        {(data ?? []).map((w) => (
          <button
            key={w.id}
            onClick={() => navigate(`${prefix}/workspace/${w.folderId}`)}
            className="flex w-full items-center gap-3 border-b themed-border-secondary px-4 py-3 text-left active:themed-bg-tertiary"
          >
            <LayoutGrid size={20} className="shrink-0 text-purple-400" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] themed-text">{getWorkspaceName(w.name)}</span>
              {w.path && (
                <span className="block truncate text-xs themed-text-muted">{w.path}</span>
              )}
            </span>
          </button>
        ))}
      </main>
    </div>
  )
}
